import { useCallback } from 'react';
import { useUploadFile } from '@/hooks/use-upload-file';
import { useVideoFrameExtractor } from '@/hooks/use-video-frame-extractor';
import { cropFrameDataUrl } from '@/lib/cover/cropFrameDataUrl';
import type { CoverCropFraming } from '@/lib/cover/resolveCropFramingAtTime';
import { frameDataUrlToFile, resolveCaptureTime } from '@/lib/media/frameCapture';

// Full composition resolution. `fitToSource` keeps the source's own aspect
// (a portrait source stays 1080×1920) instead of center-cropping it into the
// 1920×1080 box; the worker cover-fits the image to the rendered size.
const CAPTURE_OPTIONS = {
  canvasWidth: 1920,
  canvasHeight: 1080,
  quality: 0.92,
  sampleFps: 30,
  fitToSource: true,
  // Full-resolution data URLs are large; there is no reason to keep many.
  maxCachedFrames: 4,
};

export type CoverFrameCaptureResult =
  | { ok: true; url: string; captureTime: number; fileName: string }
  | { ok: false; error: 'captureError' | 'uploadError' | 'cancelled' };

export interface CaptureAndUploadOptions {
  /** Checked between async steps; returning false abandons the capture
   *  (resolves `cancelled`) so an unmounted caller never uploads. */
  isActive?: () => boolean;
  /** Called when the upload begins. */
  onUploadStart?: () => void;
}

export interface UseCoverFrameCaptureInput {
  /** Full-quality source video used for the frame capture. */
  videoUrl: string | undefined;
  /** width/height of the export — the captured frame is cropped to it. */
  exportAspect: number;
  /** The preview's reframe (center and zoom) at a source time, or null. */
  resolveCropFraming?: (sourceTime: number) => CoverCropFraming | null;
}

/**
 * The single capture → crop → upload path for cover frames, shared by
 * "Usar frame atual" and the AI cover reference: `captureFramedDataUrl`
 * renders the source-video candidate, `uploadFramedDataUrl` uploads whichever
 * candidate the user picked. It deliberately does NOT
 * register the upload in My Media: the manual cover registers its own row,
 * while the AI reference frame must never create one.
 */
export function useCoverFrameCapture({
  videoUrl,
  exportAspect,
  resolveCropFraming,
}: UseCoverFrameCaptureInput) {
  const { getUploadUrl } = useUploadFile();
  const { extractFrame } = useVideoFrameExtractor(videoUrl, CAPTURE_OPTIONS);

  /** Extracts the frame at `time` and crops it to the export aspect around
   *  the preview's reframe center and zoom. Crop failure keeps the full frame
   *  (the worker still cover-fits it, centered). Null when extraction fails. */
  const captureFramedDataUrl = useCallback(
    async (time: number): Promise<{ dataUrl: string; captureTime: number } | null> => {
      if (!videoUrl) return null;
      const captureTime = resolveCaptureTime({ videoUrl, time });
      const dataUrl = await extractFrame(captureTime);
      if (!dataUrl) return null;
      try {
        const framing = resolveCropFraming?.(captureTime) ?? null;
        const framed = await cropFrameDataUrl(dataUrl, {
          targetAspect: exportAspect,
          centerX: framing?.centerX ?? null,
          centerY: framing?.centerY ?? null,
          zoom: framing?.zoom ?? 1,
        });
        return { dataUrl: framed, captureTime };
      } catch {
        return { dataUrl, captureTime };
      }
    },
    [videoUrl, extractFrame, exportAspect, resolveCropFraming],
  );

  /** Uploads an already-rendered cover frame (either candidate: the preview
   *  still or the cropped source frame). Never registers it in My Media. */
  const uploadFramedDataUrl = useCallback(
    async (
      dataUrl: string,
      captureTime: number,
      options: CaptureAndUploadOptions = {},
    ): Promise<CoverFrameCaptureResult> => {
      const isActive = options.isActive ?? (() => true);
      if (!isActive()) return { ok: false, error: 'cancelled' };
      const fileName = `cover-frame-${Math.round(captureTime * 1000)}.jpg`;
      let file: File;
      try {
        const frame = frameDataUrlToFile(dataUrl, captureTime);
        file = new File([frame], fileName, { type: frame.type });
      } catch {
        return { ok: false, error: 'captureError' };
      }
      try {
        options.onUploadStart?.();
        const url = await getUploadUrl(file);
        if (!isActive()) return { ok: false, error: 'cancelled' };
        if (!url) return { ok: false, error: 'uploadError' };
        return { ok: true, url, captureTime, fileName };
      } catch {
        return isActive() ? { ok: false, error: 'uploadError' } : { ok: false, error: 'cancelled' };
      }
    },
    [getUploadUrl],
  );

  return { captureFramedDataUrl, uploadFramedDataUrl };
}
